import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, MapPin, ShieldCheck,
  TrendingDown, Radio, Users, Wallet, CheckCircle2
} from "lucide-react";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import { CmsImageSlideshow as CmsImage } from "../lib/images";
import { CmsText } from "../lib/texts";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { waProps, waLabel } from "../lib/whatsapp";

/**
 * Dedicated editorial page for the MerchFinder service.
 *
 * Text is client's verbatim brochure copy adapted to a modern cinematic
 * layout. Three client-supplied photos are used:
 *   • Hero  — highway sunset with cargo truck (transit context).
 *   • Block 1 — fleet vehicles (device is transport-agnostic).
 *   • Block 2 — merchandise inside trailers (what we actually protect).
 */
export default function MerchFinder() {
  const { pick } = useI18n();
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data?.data || null)).catch(() => {});
    api.post("/track/visit", { path: "/servicios/merch-finder" }).catch(() => {});
  }, []);

  // Verbatim brochure copy (kept in ES) + faithful EN / PT translations
  const P1_ES = "Merchfinder es un sistema integral de localización de mercancía, por medio del cual usted podrá localizarla de forma totalmente independiente del tractocamión o transporte que utilice.";
  const P1_EN = "MerchFinder is an integrated merchandise-tracking system that lets you locate your cargo completely independently from the truck or transport you're using.";
  const P1_PT = "O MerchFinder é um sistema integral de localização de mercadoria através do qual você pode localizá-la de forma totalmente independente do cavalo mecânico ou transporte utilizado.";

  const P2_ES = "Merchfinder no requiere de instalación alguna y su tamaño reducido (6.5 cm × 4 cm) alberga lo último en tecnología de localización GPS, haciéndolo una solución robusta y flexible para utilizarlo de forma fácil y totalmente oculto — nadie sabrá que la mercancía está protegida y vigilada.";
  const P2_EN = "MerchFinder requires no installation at all. Its compact size (6.5 cm × 4 cm) holds the latest GPS location technology, making it a robust, flexible solution to deploy easily and completely hidden — no one will know that your merchandise is protected and monitored.";
  const P2_PT = "O MerchFinder não requer nenhuma instalação, e seu tamanho reduzido (6,5 cm × 4 cm) abriga o que há de mais avançado em tecnologia GPS, sendo uma solução robusta e flexível para usar de forma fácil e totalmente oculta — ninguém saberá que a mercadoria está protegida e vigiada.";

  const P3_ES = "Genera cercas virtuales con radios predeterminados para saber al instante si la mercancía monitoreada sale de una zona geográfica definida. Puede configurarse para dar localizaciones en intervalos muy cortos — hasta cada 5 segundos o cada 5 metros de movimiento, lo que suceda primero — dándote control total desde nuestro centro de monitoreo o desde tu oficina vía internet.";
  const P3_EN = "Generate virtual geofences with pre-defined radii to know the instant your monitored cargo leaves a defined area. Configure it for very short update intervals — as fast as every 5 seconds or every 5 meters of movement, whichever happens first — giving you total control from our monitoring center or from your office over the internet.";
  const P3_PT = "Gere cercas eletrônicas com raios pré-definidos para saber na hora se a mercadoria monitorada sai de uma área geográfica definida. Pode ser configurado para dar localizações em intervalos muito curtos — a cada 5 segundos ou 5 metros de movimento, o que acontecer primeiro — dando controle total pela nossa central de monitoramento ou pelo seu escritório via internet.";

  const OP_ES = "En cuanto se notifica un reporte de robo o pérdida a nuestro centro de monitoreo, en cuestión de segundos rastreamos y localizamos la mercancía en movimiento. En paralelo hacemos un enlace directo con las autoridades correspondientes, a las cuales se les provee la ubicación exacta para que ejecuten el operativo de recuperación.";
  const OP_EN = "The moment a theft or loss is reported to our monitoring center, we track and locate the moving cargo in seconds. In parallel, we link with the corresponding authorities and hand over the exact location so they can execute the recovery operation.";
  const OP_PT = "Assim que um reporte de roubo ou perda é comunicado à nossa central de monitoramento, em segundos rastreamos e localizamos a mercadoria em movimento. Em paralelo, fazemos um enlace com as autoridades competentes, às quais é fornecida a localização exata para que executem a operação de recuperação.";

  const BENEFITS = [
    {
      Icon: Wallet,
      title: pick("Menor prima de seguro", "Lower insurance premium", "Menor prêmio de seguro"),
      body: pick("Baja considerablemente los precios de póliza de seguro.", "Substantially lowers your insurance policy prices.", "Reduz consideravelmente os preços da apólice de seguro."),
    },
    {
      Icon: ShieldCheck,
      title: pick("Tranquilidad carretera", "Peace of mind on the road", "Tranquilidade na estrada"),
      body: pick("Tranquilidad y seguridad ante el alto índice de robo de mercancía en sistemas carreteros.", "Peace of mind and security given the high rate of cargo theft on highways.", "Tranquilidade e segurança diante do alto índice de roubo de mercadoria nas rodovias."),
    },
    {
      Icon: TrendingDown,
      title: pick("Eficiencia operativa", "Operational efficiency", "Eficiência operacional"),
      body: pick("Incremento de eficiencia valorando los diferentes sistemas de transporte.", "Increased efficiency by evaluating different transport systems.", "Aumento da eficiência avaliando os diferentes sistemas de transporte."),
    },
    {
      Icon: Users,
      title: pick("Anti-colusión interna", "Anti-collusion", "Anticolusão"),
      body: pick("Evita la colusión del personal de la empresa con la delincuencia organizada.", "Prevents collusion between company personnel and organized crime.", "Evita o conluio entre funcionários da empresa e o crime organizado."),
    },
    {
      Icon: Radio,
      title: pick("Reducción de costos de seguridad", "Security cost reduction", "Redução dos custos de segurança"),
      body: pick("Menos vigilancia física y más inteligencia de datos — reducción real en costos.", "Less physical guarding, more data intelligence — real cost reduction.", "Menos vigilância física e mais inteligência de dados — redução real de custos."),
    },
  ];

  return (
    <div className="min-h-screen text-white" data-testid="merch-finder-page">
      <NavBar />

      {/* ═══════ HERO ═══════ */}
      <section className="relative overflow-hidden h-[62vh] min-h-[440px] max-h-[720px] bg-[#06080D]">
        <CmsImage
          slotKey="merch.hero"
          defaultSrc="/merch-finder/highway-sunset.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-75"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#06080D] via-[#06080D]/70 to-[#06080D]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#06080D] via-transparent to-transparent" />
        <div className="absolute inset-0 grid-bg opacity-[0.08]" />

        <div className="relative h-full max-w-7xl mx-auto px-5 sm:px-6 md:px-10 flex items-end pb-10 md:pb-14 pt-32 sm:pt-36 md:pt-40">
          <div className="max-w-2xl">
            <Link to="/servicios" className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-300 transition mb-4" data-testid="back-to-services">
              <ArrowLeft className="w-4 h-4" />
              <span className="label-mono">{pick("SERVICIOS / ALL", "SERVICES / ALL", "SERVIÇOS / ALL")}</span>
            </Link>
          </div>
        </div>

        {/* Centered page title over the banner */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 flex items-center justify-center pointer-events-none px-4"
          data-testid="merch-banner-title"
        >
          <span
            className="font-display chrome-text text-4xl sm:text-6xl md:text-7xl lg:text-[7rem] tracking-[0.08em] leading-none text-center whitespace-nowrap"
            style={{ filter: 'drop-shadow(0 4px 24px rgba(0,0,0,0.85))' }}
          >
            MERCH FINDER
          </span>
        </motion.h1>
        <div className="absolute bottom-0 inset-x-0 h-10 bg-gradient-to-b from-transparent to-[#06080D] pointer-events-none" />
      </section>

      {/* ═══════ BLOCK #1 — Independent of transport ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="absolute top-1/3 -left-40 w-[560px] h-[560px] rounded-full bg-cyan-500/8 blur-[160px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: .7 }}
              className="lg:col-span-5 relative"
            >
              <div className="relative rounded-2xl overflow-hidden chrome-border shadow-[0_25px_60px_-15px_rgba(0,240,255,0.15)]">
                <CmsImage
                  slotKey="merch.tunnel"
                  defaultSrc="/merch-finder/fleet-tunnel.jpg"
                  alt={pick("Independiente del transporte", "Transport-agnostic", "Independente do transporte")}
                  className="w-full h-full object-cover aspect-square"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#06080D]/40 via-transparent to-cyan-500/10 mix-blend-multiply" />
                <div className="absolute bottom-4 left-4 chrome-border rounded-xl px-3 py-2 flex items-center gap-2" style={{ background: 'rgba(6,8,13,0.85)', backdropFilter: 'blur(8px)' }}>
                  <span className="pulse-dot" />
                  <span className="label-mono !text-cyan-300 text-[10px]">{pick("6.5 CM × 4 CM · 100% OCULTO", "6.5 CM × 4 CM · FULLY HIDDEN", "6,5 CM × 4 CM · 100% OCULTO")}</span>
                </div>
              </div>
              <div className="absolute -top-2 -left-2 w-6 h-6 border-t border-l border-cyan-400/40 pointer-events-none" />
              <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b border-r border-cyan-400/40 pointer-events-none" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: .7, delay: .1 }}
              className="lg:col-span-7 lg:-mt-24"
            >
              <p className="text-white/85 text-base sm:text-lg leading-relaxed">
                {pick(P1_ES, P1_EN, P1_PT)}
              </p>
              <p className="mt-4 text-white/85 text-base sm:text-lg leading-relaxed">
                {pick(P2_ES, P2_EN, P2_PT)}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ BLOCK #2 — Geofences + monitoring ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="absolute bottom-1/3 -right-40 w-[560px] h-[560px] rounded-full bg-emerald-400/6 blur-[160px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: .7 }}
              className="lg:col-span-7 order-2 lg:order-1"
            >
              <p className="text-white/85 text-base sm:text-lg leading-relaxed">
                {pick(P3_ES, P3_EN, P3_PT)}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: .7, delay: .1 }}
              className="lg:col-span-5 order-1 lg:order-2 relative"
            >
              <div className="relative rounded-2xl overflow-hidden chrome-border shadow-[0_25px_60px_-15px_rgba(0,240,255,0.15)]">
                <CmsImage
                  slotKey="merch.cargo"
                  defaultSrc="/merch-finder/cargo-open.jpg"
                  alt={pick("Mercancía protegida", "Protected merchandise", "Mercadoria protegida")}
                  className="w-full h-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-tl from-[#06080D]/50 via-transparent to-emerald-500/10 mix-blend-multiply" />
                <div className="absolute bottom-4 right-4 chrome-border rounded-xl px-3 py-2 flex items-center gap-2" style={{ background: 'rgba(6,8,13,0.85)', backdropFilter: 'blur(8px)' }}>
                  <MapPin className="w-3.5 h-3.5 text-emerald-300" />
                  <span className="label-mono !text-emerald-300 text-[10px]">{pick("CERCAS VIRTUALES ACTIVAS", "GEOFENCES ACTIVE", "CERCAS ELETRÔNICAS ATIVAS")}</span>
                </div>
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 border-t border-r border-cyan-400/40 pointer-events-none" />
              <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b border-l border-cyan-400/40 pointer-events-none" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ BENEFITS ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="mb-6 max-w-2xl">
            <h2 className="font-display text-3xl md:text-4xl chrome-text leading-tight">
              {pick("Lo que ganas con MerchFinder", "What you gain with MerchFinder", "O que você ganha com o MerchFinder")}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {BENEFITS.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: .5, delay: i * 0.06 }}
                className="glass-card chrome-border p-5 group hover:border-cyan-400/50 transition-colors"
                data-testid={`merch-benefit-${i}`}
              >
                <span className="w-9 h-9 rounded-lg flex items-center justify-center bg-cyan-400/15 border border-cyan-400/40 group-hover:bg-cyan-400/25 transition">
                  <b.Icon className="w-4.5 h-4.5 text-cyan-300" />
                </span>
                <h3 className="mt-4 font-tech text-sm text-white leading-tight">{b.title}</h3>
                <p className="mt-2 text-[13px] text-white/70 leading-relaxed">{b.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ OPERATION ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="relative overflow-hidden rounded-3xl chrome-border p-6 sm:p-8 md:p-12">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/8 via-transparent to-emerald-500/8 pointer-events-none" />
            <div className="relative grid lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-4">
                <h2 className="font-display text-3xl md:text-4xl chrome-text leading-tight">
                  {pick("Del reporte a la ", "From report to ", "Do reporte à ")}
                  <span className="text-cyan-300 italic">{pick("localización.", "location.", "localização.")}</span>
                </h2>
              </div>
              <div className="lg:col-span-8">
                <p className="text-white/85 text-base sm:text-lg leading-relaxed">
                  {pick(OP_ES, OP_EN, OP_PT)}
                </p>
                {/* Timeline */}
                <div className="mt-6 grid sm:grid-cols-3 gap-4">
                  {[
                    { t: pick("1. Reporte", "1. Report", "1. Reporte"), d: pick("Cliente notifica robo o pérdida al centro de monitoreo.", "Client notifies theft or loss to the monitoring center.", "Cliente notifica roubo ou perda à central de monitoramento.") },
                    { t: pick("2. Localización", "2. Locate", "2. Localização"), d: pick("En segundos rastreamos y localizamos la mercancía en movimiento.", "Within seconds we track and locate the moving cargo.", "Em segundos rastreamos e localizamos a mercadoria em movimento.") },
                    { t: pick("3. Enlace con autoridad", "3. Authority link", "3. Enlace com autoridade"), d: pick("Enlace directo con autoridades y coordinación del operativo de recuperación.", "Direct link with authorities and recovery-operation coordination.", "Enlace direto com autoridades e coordenação da operação de recuperação.") },
                  ].map((s, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-cyan-300 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-tech text-sm text-white leading-tight">{s.t}</div>
                        <p className="mt-1 text-[13px] text-white/65 leading-relaxed">{s.d}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ CTA STRIP ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="relative overflow-hidden rounded-3xl chrome-border p-8 sm:p-10 md:p-14">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-emerald-500/10 pointer-events-none" />

            {/* Ambient soft glow spot */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(0,240,255,0.10) 0%, transparent 65%)', filter: 'blur(20px)' }}
            />

            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-8 md:gap-10">
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="relative font-display leading-[1.08] tracking-tight max-w-2xl text-[1.6rem] sm:text-3xl md:text-4xl lg:text-[2.6rem] self-center"
                data-testid="merch-cta-headline"
              >
                <span
                  className="chrome-text italic font-semibold"
                  style={{
                    letterSpacing: '-0.005em',
                    filter: 'drop-shadow(0 2px 12px rgba(0,240,255,0.15)) drop-shadow(0 1px 0 rgba(0,0,0,0.6))',
                  }}
                >
                  <CmsText
                    slotKey="merch.cta"
                    es="Hablemos del sistema de localización que tu mercancía necesita."
                    en="Let's talk about the tracking system your merchandise needs."
                    pt="Vamos falar sobre o sistema de localização que sua mercadoria precisa."
                    lang={pick("es","en","pt")}
                  />
                </span>
              </motion.h3>
              <a
                {...waProps(content?.contact?.whatsapp ? `https://wa.me/${String(content.contact.whatsapp).replace(/[^0-9]/g,'')}?text=${encodeURIComponent(pick('Hola, me interesa el servicio MerchFinder','Hi, I am interested in the MerchFinder service','Olá, tenho interesse no serviço MerchFinder'))}` : "#contact")}
                className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400 text-black font-tech uppercase tracking-wider text-sm shadow-[0_0_28px_-4px_rgba(0,240,255,0.7)] hover:shadow-[0_0_36px_-4px_rgba(0,240,255,0.95)] transition self-start md:self-auto"
                data-testid="merch-cta-whatsapp"
              >
                {waLabel(pick, "Escribir por WhatsApp", "Message on WhatsApp", "Falar pelo WhatsApp")}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer content={content} />
      <FloatingWhatsApp phone={content?.contact?.whatsapp} />
    </div>
  );
}
